import React, { memo, useMemo, useState } from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { USERS } from "../../config/apiConfig";
import DrawerLoading from "../DrawerLoading";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import { FaCar, FaSearch, FaTools } from "react-icons/fa";
import CustomVehicleQuery from "../queries/CustomVehicleQuery";
import { motion } from "framer-motion";
import { Tooltip } from "react-tooltip";
import { formatToReal } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";

const truncateText = (value, limit = 80) => {
  if (!value) return "";
  return value.length > limit ? `${value.slice(0, limit - 3)}...` : value;
};

const QueriesAutoTable = () => {
  const { user } = useApplication();
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isListVisible, setIsListVisible] = useState(true);

  const { isLoading, data: userQueryPermissions } = useQuery(
    ["userQueryPermissions", "auto"],
    async () => {
      const { data: items } = await axios.get(
        `${USERS.GET_USER_QUERY_PERMISSIONS}`
      );
      return items.data.filter(
        (query) => query.categoryId === 3 && query.status != -1
      );
    },
    {
      refetchOnMount: "always",
      staleTime: 0,
    }
  );

  const filteredQueries = userQueryPermissions?.filter(
    (query) =>
      query?.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !query.isModular
  );

  const sortedQueries = filteredQueries?.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const crlvQueries = sortedQueries?.filter((q) =>
    q.name.toLowerCase().includes("crlv")
  );
  const atpvQueries = sortedQueries?.filter((q) =>
    q.name.toLowerCase().includes("atpv")
  );
  const otherVehicleQueries = sortedQueries?.filter(
    (q) =>
      !q.name.toLowerCase().includes("crlv") &&
      !q.name.toLowerCase().includes("atpv")
  );

  const handleQueryClick = (query) => {
    setSelectedQuery(query);
    setIsListVisible(false);
  };

  const handleBackClick = () => {
    setIsListVisible(true);
    setSelectedQuery(null);
  };

  const QueryCard = memo(function QueryCard({ query, index }) {
    const statusActive = query?.status == 1;
    const showPrice =
      user?.Company?.showPriceQuery && Number.isFinite(Number(query?.price));
    const priceLabel = showPrice
      ? `${formatToReal(query.price)} por consulta`
      : "Preço sob consulta";
    const tags = useMemo(
      () =>
        (query?.tags || [])
          .map((tag) => (typeof tag === "string" ? tag : tag?.name))
          .filter(Boolean)
          .slice(0, 3),
      [query]
    );

    return (
      <motion.div
        key={query.id}
        initial={{ opacity: 0, scale: 0.97, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.35, delay: index * 0.04 }}
        onClick={statusActive ? () => handleQueryClick(query) : undefined}
        className={`relative group rounded-3xl border bg-white p-4 shadow-sm transition-all duration-300 ${
          statusActive
            ? "border-slate-200 hover:-translate-y-1 hover:shadow-md cursor-pointer"
            : "border-gray-300 opacity-60 pointer-events-none"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <span
              className={`rounded-full p-2 ${
                statusActive ? "bg-blue-50" : "bg-gray-100"
              }`}
            >
              <FaCar
                className={`text-xl ${
                  statusActive ? "text-blue-700" : "text-gray-500"
                }`}
              />
            </span>
            <div className="min-w-0 flex-1">
              <h3
                className="text-[13px] font-semibold text-slate-900 uppercase truncate overflow-hidden max-w-[220px] "
                title={query.name}
              >
                {query.name}
              </h3>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Consulta Veicular
              </p>
            </div>
          </div>
        </div>

        <p className="mt-3 text-[11px] text-slate-500">
          {truncateText(
            query?.description ||
              "Consulta veicular com dados oficiais e atualizados.",
            80
          )}
        </p>

        <div className="mt-4 flex items-center justify-between text-[12px] text-slate-500">
          <span className="font-semibold text-slate-900">{priceLabel}</span>
          {statusActive && (
            <span className="text-[11px] font-semibold text-white rounded-full bg-gradient-to-r from-blue-600 to-blue-800 px-3 py-1 shadow-sm">
              CONSULTAR
            </span>
          )}
        </div>
      </motion.div>
    );
  });

  if (isLoading) return <DrawerLoading />;
  if (!userQueryPermissions?.length) return <ForbiddenQueriesPermission />;

  return (
    <div className="w-full">
      {isListVisible ? (
        <>
          <div className="mb-3 relative">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar consultas veiculares..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-600 focus:outline-none text-sm sm:text-base placeholder-gray-400 transition-all"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-700 pointer-events-none">
                <FaSearch size={18} />
              </div>
            </div>
          </div>

          {otherVehicleQueries?.length > 0 && (
            <>
              <h2 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                🚘 <span>Consultas Gerais</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200">
                {otherVehicleQueries.map((query, index) => (
                  <QueryCard query={query} index={index} />
                ))}
              </div>
            </>
          )}

          {crlvQueries?.length > 0 && (
            <>
              <h2 className="text-xl font-bold text-green-700 mb-3 flex items-center gap-2">
                📄 <span>CRLV</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200">
                {crlvQueries.map((query, index) => (
                  <QueryCard query={query} index={index} />
                ))}
              </div>
            </>
          )}

          {atpvQueries?.length > 0 && (
            <>
              <h2 className="text-xl font-bold text-yellow-700 mb-3 flex items-center gap-2">
                📝 <span>ATPV</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200">
                {atpvQueries.map((query, index) => (
                  <QueryCard query={query} index={index} />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div>
          <button
            onClick={handleBackClick}
            className="flex items-center text-sm text-green-700 hover:text-green-900 font-medium mb-4 transition-all"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar para a listagem
          </button>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.4 }}
          >
            <CustomVehicleQuery query={selectedQuery} selectedPrice={10} />
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default QueriesAutoTable;
